import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Grid, Typography, IconButton, Button, Chip, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Avatar, TextField, InputAdornment, Menu,
    MenuItem, Tooltip, Badge, Popover
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import EventIcon from '@mui/icons-material/Event';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import http from '../../Api/http';
import { getLeaveApprovalDashboard, GetLeaveTypes } from '../../Api/Api';
import useFinancialYear from '../../hooks/useFinancialYear';
import SnackBar from '../SnackBar';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import { selectUserTypeId, USER_TYPE, selectEffectiveModules } from '../../redux/slices/authSlice';
import { hasModule } from '../../data/serverModules';
import ApplyLeavePage from './ApplyLeavePage';
import ApprovalWorkflowPage from './ApprovalWorkflowPage';
import { SkeletonRows } from '../uiKit';
import MyLeaveStatusPage from './MyLeaveStatusPage';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_DARK = '#0E4A75';
const PRIMARY_BORDER = '#B3D3E8';

const STATUS_STYLE = {
    Approved: { color: '#0E4A75', bg: '#E4F0F9', border: '#B3D3E8', icon: CheckCircleIcon },
    Pending:  { color: '#B45309', bg: '#E0F2FE', border: '#BAE6FD', icon: PendingIcon },
    Rejected: { color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', icon: CancelIcon },
};

const LEAVE_TYPE_STYLE = {
    'Sick Leave':      { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
    'Casual Leave':    { color: '#0E4A75', bg: '#E4F0F9', border: '#B3D3E8' },
    'Planned Leave':   { color: '#16A34A', bg: '#EFF5FF', border: '#B3D3E8' },
    'Emergency Leave': { color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
};

const AVATAR_PALETTE = ['#0E7490', '#1D4ED8', '#C2410C', '#0E4A75', '#1D4ED8', '#BE185D', '#A16207', '#0F766E'];
const avatarColorFor = (name = '') => {
    const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
    return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const normalizeLeaveStatus = (s) => {
    if (!s) return 'Pending';
    const v = String(s).toLowerCase();
    if (v.startsWith('approve')) return 'Approved';
    if (v.startsWith('reject'))  return 'Rejected';
    return 'Pending';
};

const PERIOD_LABEL = {
    'Monthly':     'month',
    'Quarterly':   'quarter',
    'Half-Yearly': 'half-year',
    'HalfYearly':  'half-year',
    'Yearly':      'year',
};

const periodToLabel = (p) => PERIOD_LABEL[p] || 'year';

const formatAllocation = (lt) => {
    const days = Number(lt?.daysPerPeriod) || 0;
    if (days === 0) return { value: 'On Demand', unit: '' };
    const period = periodToLabel(lt?.allocationPeriod);
    return { value: `${days} ${days === 1 ? 'day' : 'days'}`, unit: `/ ${period}` };
};

const SUB_TABS = [
    { key: 'allLeaves',       label: 'All Leaves',  color: '#374151', icon: null },
    { key: 'pendingApproval', label: 'Pending',     color: '#B45309', icon: PendingIcon },
    { key: 'approved',        label: 'Approved',    color: '#0E4A75', icon: CheckCircleIcon },
    { key: 'rejected',        label: 'Rejected',    color: '#B91C1C', icon: CancelIcon },
];

export default function LeaveManagementPage({
    isEmbedded = false,
    initialSubView = 'applications',
    onSubViewChange,
}) {
    const user = useSelector((state) => state.auth);
    const userType = user.userType;
    const role = (user.role || '').toLowerCase();
    const isSuperAdmin = userType === 'superadmin' || role.includes('super');
    const userTypeId = useSelector(selectUserTypeId);
    const isMasterAdmin = userTypeId === USER_TYPE.MASTER_ADMIN;
    const isAdmin = userTypeId !== USER_TYPE.EMPLOYEE;
    const isEmployee = userTypeId === USER_TYPE.EMPLOYEE;

    const modules = useSelector(selectEffectiveModules);
    const can = (key) => hasModule(modules, key);

    const [subView, setSubViewState] = useState(initialSubView);
    useEffect(() => {
        if (initialSubView && initialSubView !== subView) setSubViewState(initialSubView);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSubView]);
    const setSubView = (v) => {
        setSubViewState(v);
        if (onSubViewChange) onSubViewChange(v);
    };
    const allowedView = {
        applications: !isEmployee && can('leave-applications'),
        apply: !isMasterAdmin && can('leave-apply'),
        myStatus: !isSuperAdmin && !isMasterAdmin && can('leave-my-requests'),
        approval: isAdmin && can('leave-approval'),
    };
    const fallbackView = ['applications', 'apply', 'myStatus', 'approval']
        .find((v) => allowedView[v]) || 'applications';
    const needsRedirect = !allowedView[subView];
    const effectiveSubView = needsRedirect ? fallbackView : subView;

    const [leaveTab, setLeaveTab] = useState(0);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [typesAnchor, setTypesAnchor] = useState(null);
    const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);

    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(false);
    const [color, setColor] = useState(false);
    const [message, setMessage] = useState('');
    const showSnack = (msg, ok = true) => { setMessage(msg); setColor(ok); setStatus(ok); setOpen(true); };

    const [dashboardData, setDashboardData] = useState({
        cards: { totalLeavesThisMonth: 0, approved: 0, pending: 0, rejected: 0, approvedPercentOfTotal: 0 },
        leaveBalance: [],
        applications: { allLeaves: [], pendingApproval: [], approved: [], rejected: [] },
    });
    const [isFetching, setIsFetching] = useState(false);

    const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);
    const [isFetchingLeaveTypes, setIsFetchingLeaveTypes] = useState(false);

    const financialYear = useFinancialYear();

    // The approval dashboard + leave-type master are admin-only endpoints and feed
    // only the "applications" view; employees get a 403 if we call them.
    const canLoadApplications = allowedView.applications && effectiveSubView === 'applications';

    const fetchDashboard = async () => {
        if (!financialYear) return;
        setIsFetching(true);
        try {
            const res = await http.get(getLeaveApprovalDashboard, {
                params: { financialYear },
            });
            const body = res?.data;
            if (!body || body.error) {
                setDashboardData(prev => ({
                    ...prev,
                    cards: { totalLeavesThisMonth: 0, approved: 0, pending: 0, rejected: 0, approvedPercentOfTotal: 0 },
                    applications: { allLeaves: [], pendingApproval: [], approved: [], rejected: [] },
                }));
                return;
            }

            const apiCards = body.cards || {};
            const pending   = Array.isArray(body.pending)  ? body.pending  : [];
            const approved  = Array.isArray(body.approved) ? body.approved : [];
            const rejected  = Array.isArray(body.rejected) ? body.rejected : [];

            const stamp = (rows, status) => rows.map(r => ({
                ...r,
                status: normalizeLeaveStatus(r.status) || status,
                forRollNumber: r.forRollNumber ?? r.employeeCode ?? '',
            }));
            const pendingRows  = stamp(pending,  'Pending');
            const approvedRows = stamp(approved, 'Approved');
            const rejectedRows = stamp(rejected, 'Rejected');

            const pendingCount  = Number(apiCards.pendingCount)  || pendingRows.length;
            const approvedCount = Number(apiCards.approvedCount) || approvedRows.length;
            const rejectedCount = Number(apiCards.rejectedCount) || rejectedRows.length;
            const total = pendingCount + approvedCount + rejectedCount;
            const approvedPct = total > 0 ? Math.round((approvedCount / total) * 100) : 0;

            setDashboardData(prev => ({
                ...prev,
                cards: {
                    totalLeavesThisMonth: total,
                    approved: approvedCount,
                    pending:  pendingCount,
                    rejected: rejectedCount,
                    approvedPercentOfTotal: approvedPct,
                },
                applications: {
                    allLeaves:       [...pendingRows, ...approvedRows, ...rejectedRows],
                    pendingApproval: pendingRows,
                    approved:        approvedRows,
                    rejected:        rejectedRows,
                },
            }));
        } catch (error) {
            console.error('Error fetching leave approval dashboard:', error);
            setDashboardData(prev => ({
                ...prev,
                cards: { totalLeavesThisMonth: 0, approved: 0, pending: 0, rejected: 0, approvedPercentOfTotal: 0 },
                applications: { allLeaves: [], pendingApproval: [], approved: [], rejected: [] },
            }));
        } finally {
            setIsFetching(false);
        }
    };

    const fetchLeaveTypes = async () => {
        if (!financialYear) return;
        setIsFetchingLeaveTypes(true);
        try {
            const res = await http.get(GetLeaveTypes, {
                params: { financialYear },
            });
            const body = res?.data;
            const list = Array.isArray(body) ? body
                : Array.isArray(body?.data?.items) ? body.data.items
                : Array.isArray(body?.data) ? body.data
                : Array.isArray(body?.items) ? body.items
                : Array.isArray(body?.leaveTypes) ? body.leaveTypes
                : [];
            setAvailableLeaveTypes(list);
        } catch (error) {
            console.error('Error fetching leave types:', error);
            setAvailableLeaveTypes([]);
        } finally {
            setIsFetchingLeaveTypes(false);
        }
    };

    useEffect(() => {
        if (!canLoadApplications) return;
        fetchLeaveTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [financialYear, canLoadApplications]);

    useEffect(() => {
        if (!canLoadApplications) return;
        fetchDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [financialYear, canLoadApplications]);

    const stats = dashboardData.cards;
    const subTabKey = SUB_TABS[leaveTab].key;
    const rawLeaves = dashboardData.applications[subTabKey] || [];

    const q = search.trim().toLowerCase();
    const filteredLeaves = rawLeaves.filter(l => {
        const matchSearch = !q
            || (l.name || '').toLowerCase().includes(q)
            || String(l.forRollNumber || '').toLowerCase().includes(q)
            || (l.reason || '').toLowerCase().includes(q);
        const matchType = typeFilter === 'all' || l.leaveType === typeFilter;
        return matchSearch && matchType;
    });

    const searchActive = q.length > 0;
    const typeActive = typeFilter !== 'all';
    const anyFilterActive = searchActive || typeActive;
    const activeTypeColor = (LEAVE_TYPE_STYLE[typeFilter]?.color) || '#6B7280';

    const allLeavesArr = dashboardData.applications.allLeaves || [];
    const subTabCounts = {
        allLeaves:       allLeavesArr.length,
        pendingApproval: (dashboardData.applications.pendingApproval || []).length,
        approved:        (dashboardData.applications.approved || []).length,
        rejected:        (dashboardData.applications.rejected || []).length,
    };

    const typeOptions = (() => {
        const set = new Set(allLeavesArr.map(l => l.leaveType).filter(Boolean));
        return [{ key: 'all', label: 'All Types' }, ...Array.from(set).map(t => ({ key: t, label: t }))];
    })();

    const typeCounts = allLeavesArr.reduce((acc, l) => {
        if (l.leaveType) acc[l.leaveType] = (acc[l.leaveType] || 0) + 1;
        return acc;
    }, {});

    const clearFilters = () => { setSearch(''); setTypeFilter('all'); };

    const handleExportExcel = () => {
        const rows = filteredLeaves;
        if (!rows.length) { showSnack('No leave applications to export.', false); return; }

        const tabLabel = SUB_TABS[leaveTab]?.label || 'All';
        const header = ['#', 'Staff Member', 'Roll No', 'Leave Type', 'From', 'To', 'Days', 'Reason', 'Status'];
        const body = rows.map((l, i) => [
            i + 1,
            l.name || '',
            l.forRollNumber || '',
            l.leaveType || '',
            l.fromDate || '',
            l.toDate || '',
            l.days ?? '',
            l.reason || '',
            l.status || '',
        ]);
        const aoa = [
            ['Leave Applications'],
            [`Tab: ${tabLabel}`, '', typeActive ? `Type: ${typeFilter}` : 'Type: All', '', searchActive ? `Search: ${search.trim()}` : ''],
            [`Financial Year: ${financialYear || '—'}`, '', `Total: ${rows.length}`],
            [],
            header,
            ...body,
        ];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [{ wch: 5 }, { wch: 22 }, { wch: 14 }, { wch: 16 }, { wch: 13 }, { wch: 13 }, { wch: 7 }, { wch: 40 }, { wch: 12 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Leave Applications');
        XLSX.writeFile(wb, `Leave_Applications_${tabLabel.replace(/\s+/g, '')}.xlsx`);
        showSnack(`Exported ${rows.length} leave application${rows.length > 1 ? 's' : ''} to Excel.`, true);
    };

    const handleApplyLeave = () => setSubView('apply');
    const handleApproveLeaves = () => setSubView('approval');
    const goBackToApplications = () => setSubView('applications');

    const kpiCards = [
        { label: 'Total Leaves', value: stats.totalLeavesThisMonth ?? 0, sub: 'This Month',                                     color: '#135F93', bg: '#E4F0F9', icon: EventIcon },
        { label: 'Approved',     value: stats.approved ?? 0,             sub: `${stats.approvedPercentOfTotal ?? 0}% of total`, color: '#16A34A', bg: '#DCFCE7', icon: CheckCircleIcon },
        { label: 'Pending',      value: stats.pending ?? 0,              sub: 'Needs Review',                                   color: '#F59E0B', bg: '#FFF7ED', icon: PendingIcon },
        { label: 'Rejected',     value: stats.rejected ?? 0,             sub: 'Declined',                                       color: '#E11D48', bg: '#FEE2E2', icon: CancelIcon },
    ];

    const renderStatusChip = (s) => {
        const cfg = STATUS_STYLE[s] || STATUS_STYLE.Pending;
        const Icon = cfg.icon;
        return (
            <Chip
                size="small" label={s}
                icon={<Icon sx={{ fontSize: '12px !important' }} />}
                sx={{
                    bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                    fontWeight: 700, fontSize: '10.5px', height: 22,
                    '& .MuiChip-icon': { color: 'inherit', ml: '6px' },
                }}
            />
        );
    };

    const renderLeaveTypeChip = (lt) => {
        const cfg = LEAVE_TYPE_STYLE[lt] || { color: '#374151', bg: '#F3F4F6', border: '#E5E7EB' };
        return (
            <Chip
                size="small" label={lt}
                sx={{
                    bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                    fontWeight: 600, fontSize: '10.5px', height: 22,
                }}
            />
        );
    };

    const leaveTypesButton = effectiveSubView === 'applications' ? (
        <>
            <Tooltip title="Available leave types" arrow>
                <IconButton
                    onClick={(e) => setTypesAnchor(e.currentTarget)}
                    sx={{ width: 42, height: 42, borderRadius: '7px', bgcolor: '#fff', border: '1px solid #E5E7EB', color: PRIMARY, '&:hover': { bgcolor: PRIMARY_LIGHT } }}
                >
                    <Badge badgeContent={availableLeaveTypes.length} color="primary" max={99}>
                        <EventIcon sx={{ fontSize: 20 }} />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(typesAnchor)} anchorEl={typesAnchor} onClose={() => setTypesAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { mt: 0.8, width: 340, borderRadius: '7px', border: '1px solid #E5E7EB', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' } } }}
            >
                        <Card sx={{
                            boxShadow: 'none', bgcolor: '#fff', border: 'none', borderRadius: 0,
                        }}>
                            <CardContent sx={{ pb: '12px !important' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '7px',
                                        bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <EventIcon sx={{ color: PRIMARY, fontSize: 16 }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827', lineHeight: 1.1 }}>
                                            Available Leave Types
                                        </Typography>
                                        <Typography sx={{ fontSize: '10.5px', color: '#6B7280', mt: 0.1 }}>
                                            Configured in Leave Master
                                        </Typography>
                                    </Box>
                                    {availableLeaveTypes.length > 0 && (
                                        <Chip
                                            label={availableLeaveTypes.length}
                                            size="small"
                                            sx={{
                                                height: 20, minWidth: 22, fontSize: '11px', fontWeight: 800,
                                                bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK,
                                                border: `1px solid ${PRIMARY_BORDER}`,
                                            }}
                                        />
                                    )}
                                </Box>

                                {isFetchingLeaveTypes ? (
                                    <SkeletonRows count={5} />
                                ) : availableLeaveTypes.length === 0 ? (
                                    <Box sx={{ py: 2.5, textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 600 }}>
                                            No leave types configured
                                        </Typography>
                                        <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', mt: 0.3 }}>
                                            Create them from the Leave Master screen.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                                        {availableLeaveTypes.map((lt, i) => {
                                            const color = lt.color || PRIMARY;
                                            const shortCode = (lt.shortCode || lt.name || '?').slice(0, 3).toUpperCase();
                                            const alloc = formatAllocation(lt);
                                            const onDemand = (Number(lt.daysPerPeriod) || 0) === 0;
                                            return (
                                                <Box
                                                    key={lt.id || lt._id || i}
                                                    sx={{
                                                        p: 1, borderRadius: '7px',
                                                        bgcolor: '#FAFAFA', border: '1px solid #E5E7EB',
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        transition: 'all 0.15s',
                                                        '&:hover': {
                                                            bgcolor: `${color}08`,
                                                            borderColor: `${color}40`,
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{
                                                        minWidth: 36, height: 30, borderRadius: '7px', px: 0.7,
                                                        bgcolor: `${color}15`, border: `1px solid ${color}40`,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}>
                                                        <Typography sx={{
                                                            fontSize: '10.5px', fontWeight: 800,
                                                            color, letterSpacing: 0.4, fontFamily: 'monospace',
                                                        }}>
                                                            {shortCode}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography sx={{
                                                            fontSize: '12px', fontWeight: 700, color: '#111827',
                                                            lineHeight: 1.15,
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {lt.name || '—'}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4, mt: 0.1 }}>
                                                            <Typography sx={{
                                                                fontSize: onDemand ? '10.5px' : '12px',
                                                                fontWeight: 800,
                                                                color: onDemand ? '#9CA3AF' : color,
                                                                fontFamily: onDemand ? 'inherit' : 'monospace',
                                                            }}>
                                                                {alloc.value}
                                                            </Typography>
                                                            {alloc.unit && (
                                                                <Typography sx={{ fontSize: '10px', color: '#6B7280', fontWeight: 600 }}>
                                                                    {alloc.unit}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <ChevronRightIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
            </Popover>
        </>
    ) : null;

    const subViewTabs = (() => {
                    const SUB_VIEWS = [
                        ...(!isEmployee && can('leave-applications') ? [
                            { key: 'applications', label: 'Applications', icon: ListAltOutlinedIcon, color: PRIMARY },
                        ] : []),
                        ...(!isMasterAdmin && can('leave-apply') ? [
                            { key: 'apply',    label: 'Apply Leave',  icon: EventNoteOutlinedIcon, color: '#135F93' },
                        ] : []),
                        ...(!isSuperAdmin && !isMasterAdmin && can('leave-my-requests') ? [
                            { key: 'myStatus', label: 'My Requests', icon: HistoryToggleOffIcon, color: '#2563EB' },
                        ] : []),
                        ...(isAdmin && can('leave-approval') ? [
                            { key: 'approval', label: 'Leave Approval', icon: FactCheckOutlinedIcon, color: '#0E4A75' },
                        ] : []),
                    ];
                    return (
                        <Box sx={{
                            display: 'flex', gap: 0.6,
                            p: 0.4, borderRadius: '7px',
                            bgcolor: '#F3F4F6', border: '1px solid #E5E7EB',
                            width: 'fit-content', flexWrap: 'wrap',
                        }}>
                            {SUB_VIEWS.map((v) => {
                                const Icon = v.icon;
                                const active = effectiveSubView === v.key;
                                return (
                                    <Box
                                        key={v.key}
                                        onClick={() => setSubView(v.key)}
                                        sx={{
                                            px: 1.6, py: 0.7, borderRadius: '7px', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: 0.8,
                                            transition: 'all 0.15s',
                                            bgcolor: active ? '#fff' : 'transparent',
                                            boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' : 'none',
                                            '&:hover': { bgcolor: active ? '#fff' : 'rgba(255,255,255,0.55)' },
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 16, color: active ? v.color : '#9CA3AF' }} />
                                        <Typography sx={{
                                            fontSize: '12.5px',
                                            fontWeight: active ? 700 : 600,
                                            color: active ? v.color : '#6B7280',
                                        }}>
                                            {v.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                })();

    const showApplyBtn = !isMasterAdmin && !can('leave-apply');
    const showApproveBtn = (userType === 'superadmin' || userType === 'admin') && !(isAdmin && can('leave-approval'));
    const extraActions = (
        <>
            {showApplyBtn && (
                <Button startIcon={<AddIcon />} onClick={handleApplyLeave}
                    sx={{
                        textTransform: 'none', borderRadius: '7px',
                        bgcolor: PRIMARY, color: '#fff', fontSize: 12.5, fontWeight: 700, px: 2.2, height: 42,
                        '&:hover': { bgcolor: PRIMARY_DARK },
                    }}>
                    Apply Leave
                </Button>
            )}
            {showApproveBtn && (
                <Button startIcon={<FactCheckOutlinedIcon />} onClick={handleApproveLeaves}
                    sx={{
                        textTransform: 'none', borderRadius: '7px', color: '#334155', bgcolor: '#fff',
                        border: '1px solid #E5E7EB', fontSize: 12.5, fontWeight: 700, px: 2, height: 42,
                        '&:hover': { bgcolor: '#F8FAFC' },
                    }}>
                    Approve Leaves
                </Button>
            )}
        </>
    );

    return (
        <>
            <SnackBar open={open} color={color} setOpen={setOpen} status={status} message={message} />

            <Box sx={isEmbedded ? { p: 0 } : { px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
                {isEmbedded ? (
                    <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>{leaveTypesButton}{subViewTabs}{extraActions}</Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
                        <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#111827', letterSpacing: '-0.5px' }}>
                            Leave Management
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
                            {leaveTypesButton}
                            {subViewTabs}
                            {extraActions}
                            {!isEmployee && (
                                <Button startIcon={<FileDownloadIcon />} onClick={handleExportExcel}
                                    sx={{
                                        textTransform: 'none', borderRadius: '7px', color: '#334155', bgcolor: '#fff',
                                        border: '1px solid #E5E7EB', fontSize: 12.5, fontWeight: 700, px: 2, height: 42,
                                        '&:hover': { bgcolor: '#F8FAFC' },
                                    }}>
                                    Export
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}

                {effectiveSubView === 'apply' && !isMasterAdmin && (
                    <ApplyLeavePage
                        onSuccess={goBackToApplications}
                        onCancel={goBackToApplications}
                    />
                )}

                {effectiveSubView === 'myStatus' && !isSuperAdmin && !isMasterAdmin && (
                    <MyLeaveStatusPage />
                )}

                {effectiveSubView === 'approval' && isAdmin && (
                    <ApprovalWorkflowPage isEmbedded={true} />
                )}

                {effectiveSubView === 'applications' && (
                <>
                    {userType !== 'teacher' && (
                        <Grid container spacing={2} sx={{ mb: 2.5 }}>
                            {kpiCards.map((card) => {
                                const Icon = card.icon;
                                return (
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
                                        <Box sx={{
                                            p: 2.5, borderRadius: '7px', bgcolor: card.bg,
                                            border: `1px solid ${card.color}22`,
                                            boxShadow: '0 1px 3px rgba(16,24,40,0.06)', height: '100%',
                                            transition: 'transform 0.15s, box-shadow 0.15s',
                                            '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 20px ${card.color}22` },
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: '11px', color: card.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        {card.label}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#0F172A', lineHeight: 1.2, mt: 0.5 }}>
                                                        {card.value}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '10.5px', color: '#6B7280', fontWeight: 600, mt: 0.3 }}>
                                                        {card.sub}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{
                                                    width: 44, height: 44, borderRadius: '7px',
                                                    bgcolor: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.08)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 1,
                                                }}>
                                                    <Icon sx={{ color: card.color, fontSize: 22 }} />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    <Card sx={{ border: '1px solid #E5E7EB', borderRadius: '7px', boxShadow: 'none', bgcolor: '#fff', overflow: 'hidden' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #E5E7EB' }}>
                            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <TextField
                                        size="small"
                                        placeholder="Search name, roll no, or reason..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ fontSize: 16, color: searchActive ? PRIMARY : '#9CA3AF' }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: searchActive ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.3 }}>
                                                            <CloseIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                            },
                                        }}
                                        sx={{
                                            flex: 1, minWidth: 220, maxWidth: 340,
                                            '& .MuiOutlinedInput-root': {
                                                height: 34, fontSize: '12.5px', borderRadius: '7px',
                                                bgcolor: searchActive ? PRIMARY_LIGHT : '#fff',
                                                '& fieldset': { borderColor: searchActive ? PRIMARY_BORDER : '#E5E7EB' },
                                                '&:hover fieldset': { borderColor: '#D1D5DB' },
                                                '&.Mui-focused fieldset': { borderColor: PRIMARY },
                                            },
                                        }}
                                    />

                                    <Button
                                        onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
                                        startIcon={<CategoryOutlinedIcon sx={{ fontSize: 16 }} />}
                                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            textTransform: 'none', fontSize: '12.5px', fontWeight: 600,
                                            height: 34, borderRadius: '7px', px: 1.5,
                                            color: typeActive ? '#fff' : '#374151',
                                            bgcolor: typeActive ? activeTypeColor : '#fff',
                                            border: `1px solid ${typeActive ? activeTypeColor : '#E5E7EB'}`,
                                            '&:hover': {
                                                bgcolor: typeActive ? activeTypeColor : '#F9FAFB',
                                                filter: typeActive ? 'brightness(0.95)' : 'none',
                                            },
                                        }}
                                    >
                                        {typeActive ? typeFilter : 'Leave Type'}
                                    </Button>
                                    <Menu
                                        anchorEl={typeMenuAnchor}
                                        open={Boolean(typeMenuAnchor)}
                                        onClose={() => setTypeMenuAnchor(null)}
                                        slotProps={{
                                            paper: {
                                                sx: {
                                                    mt: 0.6, borderRadius: '7px',
                                                    border: '1px solid #E5E7EB',
                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                                    minWidth: 220,
                                                },
                                            },
                                        }}
                                    >
                                        {typeOptions.map((opt) => {
                                            const isActive = typeFilter === opt.key;
                                            const cfg = LEAVE_TYPE_STYLE[opt.key] || { color: '#6B7280' };
                                            const count = opt.key === 'all' ? allLeavesArr.length : (typeCounts[opt.key] || 0);
                                            return (
                                                <MenuItem
                                                    key={opt.key}
                                                    onClick={() => { setTypeFilter(opt.key); setTypeMenuAnchor(null); }}
                                                    sx={{
                                                        py: 0.8, px: 1.2, gap: 1,
                                                        bgcolor: isActive ? `${cfg.color}10` : 'transparent',
                                                        '&:hover': { bgcolor: `${cfg.color}15` },
                                                    }}
                                                >
                                                    <CircleIcon sx={{ fontSize: 9, color: cfg.color, flexShrink: 0 }} />
                                                    <Typography sx={{
                                                        fontSize: '13px',
                                                        fontWeight: isActive ? 700 : 500,
                                                        color: isActive ? cfg.color : '#374151', flex: 1,
                                                    }}>
                                                        {opt.label}
                                                    </Typography>
                                                    <Box sx={{
                                                        px: 0.7, py: 0.1, borderRadius: '7px',
                                                        bgcolor: isActive ? '#fff' : '#F3F4F6',
                                                        border: `1px solid ${isActive ? `${cfg.color}30` : '#E5E7EB'}`,
                                                    }}>
                                                        <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: isActive ? cfg.color : '#6B7280' }}>
                                                            {count}
                                                        </Typography>
                                                    </Box>
                                                    {isActive && <CheckIcon sx={{ fontSize: 15, color: cfg.color, ml: 0.4 }} />}
                                                </MenuItem>
                                            );
                                        })}
                                    </Menu>

                                <Box sx={{
                                    display: 'flex', gap: 0.6,
                                    p: 0.4, borderRadius: '7px',
                                    bgcolor: '#F3F4F6', border: '1px solid #E5E7EB',
                                    width: 'fit-content', flexWrap: 'wrap',
                                }}>
                                    {SUB_TABS.map((t, idx) => {
                                        const Icon = t.icon;
                                        const active = leaveTab === idx;
                                        const count = subTabCounts[t.key] || 0;
                                        return (
                                            <Box
                                                key={t.key}
                                                onClick={() => setLeaveTab(idx)}
                                                sx={{
                                                    px: 1.4, py: 0.6, borderRadius: '7px', cursor: 'pointer',
                                                    display: 'flex', alignItems: 'center', gap: 0.7,
                                                    transition: 'all 0.15s',
                                                    bgcolor: active ? '#fff' : 'transparent',
                                                    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' : 'none',
                                                    '&:hover': { bgcolor: active ? '#fff' : 'rgba(255,255,255,0.55)' },
                                                }}
                                            >
                                                {Icon && <Icon sx={{ fontSize: 14, color: active ? t.color : '#9CA3AF' }} />}
                                                <Typography sx={{
                                                    fontSize: '12px',
                                                    fontWeight: active ? 700 : 600,
                                                    color: active ? t.color : '#6B7280',
                                                }}>
                                                    {t.label}
                                                </Typography>
                                                <Box sx={{
                                                    px: 0.7, py: 0.05, borderRadius: '7px', minWidth: 18, textAlign: 'center',
                                                    bgcolor: active ? `${t.color}15` : '#fff',
                                                    border: `1px solid ${active ? `${t.color}30` : '#E5E7EB'}`,
                                                }}>
                                                    <Typography sx={{ fontSize: '10px', fontWeight: 800, color: active ? t.color : '#9CA3AF' }}>
                                                        {count}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    <Typography sx={{ fontSize: '11.5px', color: '#6B7280' }}>Showing</Typography>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 800, color: anyFilterActive ? PRIMARY_DARK : '#111827' }}>
                                        {filteredLeaves.length}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11.5px', color: '#6B7280' }}>
                                        of {rawLeaves.length}
                                    </Typography>
                                </Box>

                                <Button
                                    size="small"
                                    startIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
                                    onClick={clearFilters}
                                    disabled={!anyFilterActive}
                                    sx={{
                                        textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                                        height: 34, borderRadius: '7px', px: 1.5, color: PRIMARY_DARK,
                                        '&:hover': { bgcolor: PRIMARY_LIGHT },
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            </Box>
                        </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: PRIMARY_LIGHT, borderBottom: `1px solid ${PRIMARY_BORDER}` }}>
                                                {['Staff Member', 'Leave Type', 'Duration', 'Days', 'Reason', 'Status'].map(h => (
                                                    <TableCell key={h} sx={{
                                                        fontWeight: 700, fontSize: '10px', color: PRIMARY_DARK,
                                                        textTransform: 'uppercase', whiteSpace: 'nowrap',
                                                        letterSpacing: 0.6, py: 1.3, borderBottom: 'none',
                                                    }}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {isFetching ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 5, borderBottom: 'none' }}>
                                                        <SkeletonRows count={5} p={0} />
                                                    </TableCell>
                                                </TableRow>
                                            ) : filteredLeaves.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 5, borderBottom: 'none' }}>
                                                        <Typography sx={{ fontSize: '13px', color: '#9CA3AF', fontWeight: 600 }}>
                                                            {rawLeaves.length === 0 ? 'No leave applications found' : 'No leaves match your filters'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>
                                                            {rawLeaves.length === 0 ? 'When applications are submitted, they will appear here.' : 'Try changing or clearing your filters above.'}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : filteredLeaves.map((leave, idx) => {
                                                const avColor = avatarColorFor(leave.name || '');
                                                return (
                                                    <TableRow
                                                        key={leave.leaveApplicationId || idx}
                                                        sx={{
                                                            '&:hover': { bgcolor: PRIMARY_LIGHT },
                                                            borderBottom: '1px solid #F3F4F6',
                                                            transition: 'background-color 0.15s',
                                                        }}
                                                    >
                                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                                <Avatar sx={{
                                                                    width: 34, height: 34,
                                                                    bgcolor: `${avColor}15`, color: avColor,
                                                                    fontSize: '11px', fontWeight: 700,
                                                                    border: `1px solid ${avColor}33`,
                                                                }}>
                                                                    {getInitials(leave.name)}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                                                                        {leave.name}
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 500, fontFamily: 'monospace' }}>
                                                                        {leave.forRollNumber}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                                            {renderLeaveTypeChip(leave.leaveType)}
                                                        </TableCell>
                                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                                                                {leave.fromDate}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF' }}>
                                                                to {leave.toDate}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                                            <Box sx={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                                px: 0.8, py: 0.2, borderRadius: '7px',
                                                                bgcolor: '#E4F0F9', border: '1px solid #B3D3E8',
                                                            }}>
                                                                <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#0E4A75', fontFamily: 'monospace' }}>
                                                                    {leave.days}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '10px', color: '#0E4A75', fontWeight: 600 }}>
                                                                    {leave.days > 1 ? 'days' : 'day'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6', maxWidth: 220 }}>
                                                            <Tooltip title={leave.reason || ''} arrow placement="top">
                                                                <Typography sx={{
                                                                    fontSize: '12px', color: '#4B5563',
                                                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap', maxWidth: 220,
                                                                }}>
                                                                    {leave.reason || '—'}
                                                                </Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                                            {renderStatusChip(leave.status)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                    </Card>
                </>
                )}
            </Box>
        </>
    );
}
